import React, { useEffect, useState } from 'react';
import DspxModal from '../../../../../common/DspxModal/DspxModal';
import { DatePicker, Form, Input, Select, Spin } from 'antd';
import styles from './TenantPassportDataModal.module.scss';
import moment from 'moment';
import useClientPassportInfo from '../../../../../../queries/useClientPassportInfo';
import addClientPassportInfo from '../../../../../../requests/addClientPassportInfo';
import { queryClient } from '../../../../../../../../queryClient';
import { getQueryKeys } from '../../../../../../queries/queryKeys';
import changeClientPassportInfo from '../../../../../../requests/changeClientPassportInfo';

const TenantPassportDataNode = ({
  form,
  clientId,
  visible,
  onCancel,
  ...props
}) => {
  const { resetFields, validateFieldsAndScroll } = form;

  const [initialValue, setInitialValue] = useState({});
  const [addConfirmLoading, setAddConfirmLoading] = useState(false);

  const { data: passportData, isFetching: isFetchingPassportData } =
    useClientPassportInfo(clientId, {
      enabled: visible,
      staleTime: Infinity,
    });

  useEffect(() => {
    if (passportData) {
      setInitialValue(passportData);
    }
  }, [passportData]);

  const handleClose = () => {
    resetFields();
    onCancel();
  };

  const handleSubmit = () => {
    try {
      validateFieldsAndScroll(async (err, fieldValues) => {
        if (!err) {
          setAddConfirmLoading(true);
          const { issuedAgencyCode, series, number } = fieldValues;

          const values = {
            ...fieldValues,
            series: Number(series),
            number: Number(number),
            issuedAgencyCode: Number(issuedAgencyCode.replace(/-/g, '')),
            clientId,
          };

          if (initialValue?.fullName) {
            await changeClientPassportInfo(values);
          } else {
            await addClientPassportInfo(values);
          }

          queryClient.invalidateQueries(
            getQueryKeys.clientPassportInfo(clientId)
          );

          setAddConfirmLoading(false);
          onCancel();
        }
      });
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const formatCode = (value) => {
    if (!value) return value;
    const strValue = value.toString();
    const onlyNums = strValue.replace(/[^\d]/g, '');
    if (onlyNums.length <= 3) return onlyNums;
    return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 6)}`;
  };

  return (
    <DspxModal
      destroyOnClose
      onCancel={handleClose}
      title="Данные паспорта"
      onOk={handleSubmit}
      visible={visible}
      okText={!!initialValue?.fullName ? 'Сохранить' : 'Добавить'}
      confirmLoading={isFetchingPassportData}
      {...props}
    >
      <Spin spinning={isFetchingPassportData || addConfirmLoading}>
        <div className={styles.dualFields}>
          <Form.Item
            className={styles.formItem}
            label="Серия"
          >
            {form.getFieldDecorator('series', {
              initialValue:
                (initialValue?.series && String(initialValue?.series)) || '',
              rules: [
                { required: true, message: 'Обязательное поле' },
                { len: 4, message: '4 цифры' },
              ],
            })(
              <Input
                type="text"
                maxLength={4}
                placeholder="XXXX"
              />
            )}
          </Form.Item>
          <Form.Item
            className={styles.formItem}
            label="Номер"
          >
            {form.getFieldDecorator('number', {
              initialValue:
                (initialValue?.number && String(initialValue?.number)) || '',
              rules: [
                { required: true, message: 'Обязательное поле' },
                { len: 6, message: '6 цифр' },
              ],
            })(
              <Input
                type="text"
                maxLength={6}
                placeholder="XXXXXX"
              />
            )}
          </Form.Item>
        </div>
        <Form.Item label="Кем выдан">
          {form.getFieldDecorator('issuedAgencyName', {
            initialValue: initialValue?.issuedAgencyName,
            rules: [{ required: true, message: 'Обязательное поле' }],
          })(
            <Input
              type="text"
              placeholder="XXXX РОССИИ по XXXX области"
            />
          )}
        </Form.Item>
        <div className={styles.dualFields}>
          <Form.Item
            className={styles.formItem}
            label="Дата выдачи"
          >
            {form.getFieldDecorator('dateOfIssue', {
              initialValue:
                initialValue?.dateOfIssue &&
                initialValue.dateOfIssue !== '0001-01-01T00:00:00.000Z'
                  ? moment(initialValue.dateOfIssue)
                  : undefined,

              rules: [
                { required: true, message: 'Обязательное поле' },
                {
                  validator: (_rule, value, callback) => {
                    const dateOfBirth = form.getFieldValue('birthDate');
                    if (dateOfBirth && value) {
                      const yearsDiff = moment(value).diff(
                        moment(dateOfBirth),
                        'years'
                      );
                      if (yearsDiff < 14) {
                        return callback(
                          'Дата выдачи не может быть раньше 14 лет с момента рождения'
                        );
                      }
                    }
                    callback();
                  },
                },
              ],
            })(
              <DatePicker
                placeholder="XX.XX.XXXX"
                allowClear={false}
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
              />
            )}
          </Form.Item>
          <Form.Item
            className={styles.formItem}
            label="Код подразделения"
          >
            {form.getFieldDecorator('issuedAgencyCode', {
              initialValue: initialValue?.issuedAgencyCode
                ? formatCode(initialValue.issuedAgencyCode)
                : undefined,
              rules: [
                { required: true, message: 'Обязательное поле' },
                { len: 7, message: '6 цифр' },
              ],
            })(
              <Input
                type="text"
                maxLength={7}
                onChange={(e) => {
                  const formattedValue = formatCode(e.target.value);
                  e.target.value = formattedValue;
                  form.setFieldsValue({ issuedAgencyCode: formattedValue });
                }}
                placeholder="XXX-XXX"
              />
            )}
          </Form.Item>
        </div>
        <Form.Item label="ФИО">
          {form.getFieldDecorator('fullName', {
            initialValue: initialValue?.fullName,
            rules: [{ required: true, message: 'Обязательное поле' }],
          })(
            <Input
              type="text"
              placeholder="Иванов Иван Иванович"
            />
          )}
        </Form.Item>
        <div className={styles.dualFields}>
          <Form.Item
            className={styles.formItem}
            label="Дата рождения"
          >
            {form.getFieldDecorator('birthDate', {
              initialValue:
                initialValue?.birthDate &&
                initialValue?.birthDate !== '0001-01-01T00:00:00.000Z'
                  ? moment(initialValue.birthDate)
                  : undefined,
              rules: [{ required: true, message: 'Обязательное поле' }],
            })(
              <DatePicker
                placeholder="XX.XX.XXXX"
                allowClear={false}
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
              />
            )}
          </Form.Item>
          <Form.Item
            className={styles.formItem}
            label="Пол"
          >
            {form.getFieldDecorator('sex', {
              initialValue: initialValue?.sex,
              rules: [{ required: true, message: 'Обязательное поле' }],
            })(
              <Select
                style={{ width: '100%' }}
                placeholder="Мужской/Женский"
              >
                <Select.Option value="M">Мужской</Select.Option>
                <Select.Option value="F">Женский</Select.Option>
              </Select>
            )}
          </Form.Item>
        </div>
        <Form.Item label="Место рождения">
          {form.getFieldDecorator('birthLocation', {
            initialValue: initialValue?.birthLocation,
            rules: [{ required: true, message: 'Обязательное поле' }],
          })(
            <Input
              type="text"
              placeholder="г. XXXX"
            />
          )}
        </Form.Item>
        <Form.Item label="Прописка">
          {form.getFieldDecorator('registrationLocation', {
            initialValue: initialValue?.registrationLocation,
            rules: [{ required: true, message: 'Обязательное поле' }],
          })(
            <Input
              onChange={(e) => {
                if (e.target.value === 0) {
                  e.preventDefault();
                }
              }}
              type="text"
              placeholder="г. XXXX, ул. XXXX, д. XXXX, кв. XXXX"
            />
          )}
        </Form.Item>
      </Spin>
    </DspxModal>
  );
};

export const TenantPassportDataModal = Form.create({
  name: 'tenant_passport_data',
})(TenantPassportDataNode);
